import React from 'react';
import { NewDashboardHeader } from '../../components/NewDashboardHeader';
import { Footer } from '../../components/Footer';
import { CIEN_CUADRAS_BLUE } from '../../constants';
import { HomeModernIcon, UserCircleIcon, CogIcon, PlusIcon } from '../../components/Icons';
import { AdminPropertyManagement } from '../admin/sections/AdminPropertyManagement';
import { EditPropertyModal } from '../admin/EditPropertyModal';
import { CreatePropertyModal } from '../../components/admin/CreatePropertyModal';
import { DashboardViewProps } from '../../config/routeMappings';
import { usePropertyAdminOperations } from '../../hooks/usePropertyAdminOperations';
import { AdminAppConfigManagement } from '../admin/sections/AdminAppConfigManagement';

export const PropertyAdminDashboardView: React.FC<DashboardViewProps> = ({ onLogout, appUser: adminUser, newlyAddedRange }) => {
  const {
    overallPropertyAdminMessage,
    setOverallPropertyAdminMessage,
    propertyAdminSearchCode,
    setPropertyAdminSearchCode,
    foundPropertyForAdmin,
    editingProperty,
    isLoadingSearch,
    handleSearchPropertyAdmin,
    handleSaveProperty,
    handleOpenPropertyEditModal,
    handleClosePropertyEditModal,
    isCreateModalOpen,
    setIsCreateModalOpen,
    handleCreateProperty,
  } = usePropertyAdminOperations(adminUser);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" style={{ fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
      <NewDashboardHeader onLogout={onLogout} appUser={adminUser} isNewlyAddedMode={!!newlyAddedRange} />
      <main className="flex-grow flex flex-col max-w-5xl mx-auto space-y-6 px-3 sm:px-6 lg:p-8 w-full">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold" style={{ color: CIEN_CUADRAS_BLUE }}>
            Panel de Administración de Propiedades
          </h1>
        </div>

        {overallPropertyAdminMessage && (
          <div
            className={`p-4 text-sm rounded-xl ${
              overallPropertyAdminMessage.toLowerCase().includes('error')
                ? 'bg-red-100 text-red-700 border-l-4 border-red-500'
                : 'bg-sky-100 text-sky-800 border-l-4 border-sky-500'
            }`}
            role="alert"
          >
            {overallPropertyAdminMessage}
            <button onClick={() => setOverallPropertyAdminMessage(null)} className="ml-4 float-right text-xs font-bold hover:underline">
              Descartar
            </button>
          </div>
        )}

        <section className="bg-white p-6 shadow-sm rounded-2xl border border-gray-100">
          <div className="flex items-center text-sky-600 mb-2">
            <UserCircleIcon className="w-7 h-7 mr-2" />
            <h2 className="text-lg font-bold text-gray-800">Bienvenido, {adminUser?.displayName || adminUser?.email}</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Gestiona los inmuebles mediante creación manual directa o busca y edita cualquier inmueble existente.
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-6 py-2.5 bg-[#0073b7] text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md hover:bg-sky-700 transition-all flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            Crear Inmueble Manual
          </button>
        </section>

        <section className="bg-white p-6 shadow-sm rounded-2xl border border-gray-100">
          <div className="flex items-center text-sky-600 mb-4">
            <CogIcon className="w-6 h-6 mr-2" />
            <h3 className="text-base font-bold text-gray-800">Configuración del Simulador</h3>
          </div>
          <AdminAppConfigManagement showOnly={['interest_rates']} />
        </section>

        <section className="bg-white p-6 shadow-sm rounded-2xl border border-gray-100">
          <div className="flex items-center text-sky-600 mb-4">
            <HomeModernIcon className="w-6 h-6 mr-2" />
            <h3 className="text-base font-bold text-gray-800">Buscar y Editar Inmueble</h3>
          </div>
          <AdminPropertyManagement
            propertyCodeAdminInput={propertyAdminSearchCode}
            setPropertyCodeAdminInput={setPropertyAdminSearchCode}
            onSearchPropertyAdmin={handleSearchPropertyAdmin}
            isLoadingSearch={isLoadingSearch}
            foundPropertyForAdmin={foundPropertyForAdmin}
            onEditProperty={handleOpenPropertyEditModal}
            message={null}
          />
        </section>
      </main>
      <Footer />

      {editingProperty && (
        <EditPropertyModal
          key={`edit-prop-admin-${editingProperty.id}`}
          property={editingProperty}
          isOpen={!!editingProperty}
          onSave={handleSaveProperty}
          onClose={handleClosePropertyEditModal}
        />
      )}

      {isCreateModalOpen && (
        <CreatePropertyModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSave={handleCreateProperty}
        />
      )}
    </div>
  );
};
